import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { RefreshCw, Users, Activity, CreditCard, MessageSquare, TrendingUp, UserCheck, Send, FlaskConical } from 'lucide-react';

const ADMIN_EMAILS = ['madsakjaeraas@gmail.com'];

const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v}%`);
const fmtNum = (v) => (v === null || v === undefined ? '—' : v.toLocaleString('nb-NO'));

function StatCard({ icon: Icon, label, value, sub, testid }) {
    return (
        <div className="rounded-2xl border border-sand bg-white p-5" data-testid={testid}>
            <div className="flex items-center gap-2 text-inkSoft text-xs uppercase tracking-wider">
                <Icon className="w-3.5 h-3.5" />
                {label}
            </div>
            <div className="mt-2 font-display text-3xl font-bold text-ink">{value}</div>
            {sub && <div className="text-[12px] text-inkSoft mt-1">{sub}</div>}
        </div>
    );
}

function MiniBars({ data }) {
    const max = Math.max(1, ...data.map((d) => d.count));
    return (
        <div className="flex items-end gap-1 h-20">
            {data.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                    <div
                        className="w-full bg-sage/70 hover:bg-sage rounded-sm transition-all"
                        style={{ height: `${(d.count / max) * 100}%`, minHeight: d.count > 0 ? '3px' : '1px' }}
                    />
                </div>
            ))}
        </div>
    );
}

export default function AdminAnalytics() {
    const { user, loading } = useAuth();
    const [data, setData] = useState(null);
    const [ab, setAb] = useState(null);
    const [abSig, setAbSig] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const [main, abRes, sig] = await Promise.all([
                api.get('/admin/analytics'),
                api.get('/admin/analytics/ab').catch(() => ({ data: null })),
                api.get('/admin/analytics/ab/significance').catch(() => ({ data: null })),
            ]);
            setData(main.data);
            setAb(abRes.data);
            setAbSig(sig.data);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke laste analytics');
        } finally {
            setBusy(false);
        }
    };

    const runTrialNudge = async () => {
        if (!window.confirm('Send nudge-e-poster til alle som har 20-28t igjen av trial-en?')) return;
        try {
            const res = await api.post('/admin/trial-nudge/run');
            toast.success(`Trial nudge kjørt: ${res.data.sent} sendt (${res.data.checked} sjekket)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Kunne ikke kjøre nudge');
        }
    };

    useEffect(() => {
        if (user) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    if (loading) return null;
    if (!user) return <Navigate to="/auth" replace />;
    const isAdmin = ADMIN_EMAILS.includes((user.email || '').toLowerCase());
    if (!isAdmin) return <Navigate to="/app" replace />;

    return (
        <div className="max-w-5xl mx-auto px-6 py-10 pb-24" data-testid="admin-analytics-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pilot-analytics</h1>
                    <p className="text-inkSoft mt-2 text-sm leading-relaxed max-w-xl">
                        Retention, engagement og betalingsmetrikker for piloten. Oppdateres i sanntid.
                    </p>
                    {data?.generated_at && (
                        <p className="text-[11px] text-inkSoft/70 mt-1">
                            Oppdatert {new Date(data.generated_at).toLocaleString('nb-NO')}
                        </p>
                    )}
                </div>
                <Button
                    onClick={load}
                    disabled={busy}
                    variant="outline"
                    size="sm"
                    className="rounded-full border-sand"
                    data-testid="refresh-analytics-btn"
                >
                    <RefreshCw className={`w-4 h-4 mr-2 ${busy ? 'animate-spin' : ''}`} />
                    Oppdater
                </Button>
            </div>

            <div className="mt-4 rounded-2xl border border-sage/30 bg-sage/5 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="trial-nudge-row">
                <div className="text-sm">
                    <div className="font-medium text-ink">Trial-nudge (loss-aversion)</div>
                    <div className="text-inkSoft text-xs mt-0.5">
                        Kjøres automatisk hver time. Sender én e-post til brukere som har 20-28t igjen av trial-en og ikke har betalt.
                    </div>
                </div>
                <Button
                    onClick={runTrialNudge}
                    size="sm"
                    variant="outline"
                    className="rounded-full border-sage text-sage-dark hover:bg-sage hover:text-bone"
                    data-testid="run-nudge-btn"
                >
                    <Send className="w-3.5 h-3.5 mr-2" />
                    Kjør nå
                </Button>
            </div>

            {!data ? (
                <div className="mt-10 text-inkSoft text-sm">Laster…</div>
            ) : (
                <div className="mt-8 space-y-8">
                    {/* Active users */}
                    <section>
                        <h2 className="font-medium text-ink mb-3 text-sm uppercase tracking-wider text-inkSoft">Aktive brukere</h2>
                        <div className="grid sm:grid-cols-3 gap-4">
                            <StatCard testid="stat-dau" icon={Activity} label="DAU (24t)" value={fmtNum(data.active.dau)} sub="Brukere aktive siste døgn" />
                            <StatCard testid="stat-wau" icon={Activity} label="WAU (7d)" value={fmtNum(data.active.wau)} sub="Brukere aktive siste uke" />
                            <StatCard testid="stat-mau" icon={Activity} label="MAU (30d)" value={fmtNum(data.active.mau)} sub="Brukere aktive siste 30 dager" />
                        </div>
                    </section>

                    {/* User totals */}
                    <section>
                        <h2 className="font-medium text-ink mb-3 text-sm uppercase tracking-wider text-inkSoft">Brukere totalt</h2>
                        <div className="grid sm:grid-cols-4 gap-4">
                            <StatCard testid="stat-total" icon={Users} label="Totalt" value={fmtNum(data.users.total)} />
                            <StatCard testid="stat-email" icon={Users} label="E-post" value={fmtNum(data.users.by_auth_method.email)} />
                            <StatCard testid="stat-anon" icon={Users} label="Anonyme" value={fmtNum(data.users.by_auth_method.anonymous)} />
                            <StatCard testid="stat-google" icon={Users} label="Google" value={fmtNum(data.users.by_auth_method.google)} />
                        </div>
                        <div className="grid sm:grid-cols-3 gap-4 mt-4">
                            <StatCard testid="stat-new-today" icon={UserCheck} label="Nye i dag" value={fmtNum(data.users.new_today)} />
                            <StatCard testid="stat-new-7d" icon={UserCheck} label="Nye 7 dager" value={fmtNum(data.users.new_7d)} />
                            <StatCard testid="stat-new-30d" icon={UserCheck} label="Nye 30 dager" value={fmtNum(data.users.new_30d)} />
                        </div>
                        {data.daily_signups?.length > 0 && (
                            <div className="rounded-2xl border border-sand bg-white p-5 mt-4" data-testid="daily-signups-chart">
                                <div className="text-inkSoft text-xs uppercase tracking-wider mb-3">Påmeldinger siste 14 dager</div>
                                <MiniBars data={data.daily_signups} />
                                <div className="flex justify-between text-[10px] text-inkSoft/70 mt-1">
                                    <span>{data.daily_signups[0].date}</span>
                                    <span>{data.daily_signups[data.daily_signups.length - 1].date}</span>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* Plans */}
                    <section>
                        <h2 className="font-medium text-ink mb-3 text-sm uppercase tracking-wider text-inkSoft">Planer (nåværende status)</h2>
                        <div className="grid sm:grid-cols-4 gap-4">
                            <StatCard testid="stat-trial" icon={CreditCard} label="På trial" value={fmtNum(data.plans.trial)} sub="3-dagers gratisperiode" />
                            <StatCard testid="stat-basic" icon={CreditCard} label="Basic" value={fmtNum(data.plans.basic)} sub="99 kr/mnd" />
                            <StatCard testid="stat-premium" icon={CreditCard} label="Premium" value={fmtNum(data.plans.premium)} sub="199 kr/mnd" />
                            <StatCard testid="stat-cancelling" icon={CreditCard} label="Sier opp" value={fmtNum(data.plans.cancelling)} sub="Avsluttes ved periodeslutt" />
                        </div>
                    </section>

                    {/* Engagement */}
                    <section>
                        <h2 className="font-medium text-ink mb-3 text-sm uppercase tracking-wider text-inkSoft">Engasjement</h2>
                        <div className="grid sm:grid-cols-4 gap-4">
                            <StatCard testid="stat-sessions-today" icon={MessageSquare} label="Samtaler i dag" value={fmtNum(data.engagement.sessions_today)} />
                            <StatCard testid="stat-sessions-7d" icon={MessageSquare} label="Samtaler 7d" value={fmtNum(data.engagement.sessions_7d)} />
                            <StatCard testid="stat-messages-today" icon={MessageSquare} label="Meldinger i dag" value={fmtNum(data.engagement.messages_today)} />
                            <StatCard testid="stat-messages-7d" icon={MessageSquare} label="Meldinger 7d" value={fmtNum(data.engagement.messages_7d)} />
                        </div>
                    </section>

                    {/* Retention & conversion */}
                    <section>
                        <h2 className="font-medium text-ink mb-3 text-sm uppercase tracking-wider text-inkSoft">Retention &amp; konvertering</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <StatCard
                                testid="stat-retention-d1"
                                icon={TrendingUp}
                                label="D1 retention"
                                value={fmtPct(data.retention.d1_rate_pct)}
                                sub={`${data.retention.d1_returned} av ${data.retention.d1_cohort} brukere kom tilbake etter ≥24t`}
                            />
                            <StatCard
                                testid="stat-conversion"
                                icon={TrendingUp}
                                label="Trial → betalende"
                                value={fmtPct(data.conversion.rate_pct)}
                                sub={`${data.conversion.trial_converted} av ${data.conversion.trial_ended_total} fullførte trials konverterte`}
                            />
                        </div>
                    </section>

                    {/* Personas */}
                    {Object.keys(data.personas || {}).length > 0 && (
                        <section>
                            <h2 className="font-medium text-ink mb-3 text-sm uppercase tracking-wider text-inkSoft">Persona-bruk</h2>
                            <div className="grid sm:grid-cols-3 gap-4">
                                {Object.entries(data.personas).map(([k, v]) => (
                                    <StatCard
                                        key={k}
                                        testid={`stat-persona-${k}`}
                                        icon={MessageSquare}
                                        label={k.charAt(0).toUpperCase() + k.slice(1)}
                                        value={fmtNum(v)}
                                        sub="bruksøkter totalt"
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {/* A/B test (EN landing) */}
                    {ab && ab.funnel && (
                        <section data-testid="ab-section">
                            <h2 className="font-medium text-ink mb-1 text-sm uppercase tracking-wider text-inkSoft flex items-center gap-2">
                                <FlaskConical className="w-3.5 h-3.5"/> A/B-test · /en landing
                            </h2>
                            <p className="text-xs text-inkSoft mb-3">
                                A = «Quiet companion» (loss-aversion). B = «Anti-therapy» (no waitlist, no copay).
                            </p>
                            {abSig && <ABVerdictCard sig={abSig}/>}
                            <ABFunnelTable funnel={ab.funnel} rates={ab.rates}/>
                            {abSig && <ABSignificanceTable sig={abSig}/>}
                        </section>
                    )}
                </div>
            )}
        </div>
    );
}

function ABFunnelTable({ funnel, rates }) {
    const steps = [
        { key: 'landing_view', label: 'Landing views' },
        { key: 'cta_click', label: 'CTA clicks' },
        { key: 'signup', label: 'Signups' },
        { key: 'trial_start', label: 'Trials started' },
        { key: 'first_message', label: 'First message sent' },
        { key: 'paid', label: 'Paid (currently active)' },
    ];
    const cellPct = (variant, num, denom) => {
        const n = funnel[variant][num] || 0;
        const d = funnel[variant][denom] || 0;
        if (!d) return '—';
        return `${Math.round(1000 * n / d) / 10}%`;
    };
    return (
        <div className="rounded-2xl border border-sand bg-white overflow-x-auto" data-testid="ab-funnel-table">
            <table className="w-full text-sm">
                <thead className="bg-sand/40 text-inkSoft text-xs uppercase tracking-wider">
                    <tr>
                        <th className="text-left px-4 py-3">Step</th>
                        <th className="text-right px-4 py-3">Variant A</th>
                        <th className="text-right px-4 py-3">Variant B</th>
                        <th className="text-right px-4 py-3">Δ (B − A)</th>
                    </tr>
                </thead>
                <tbody>
                    {steps.map((s) => {
                        const a = funnel.A[s.key] || 0;
                        const b = funnel.B[s.key] || 0;
                        const delta = b - a;
                        return (
                            <tr key={s.key} className="border-t border-sand" data-testid={`ab-row-${s.key}`}>
                                <td className="px-4 py-3 text-ink">{s.label}</td>
                                <td className="px-4 py-3 text-right tabular-nums" data-testid={`ab-A-${s.key}`}>{a.toLocaleString('nb-NO')}</td>
                                <td className="px-4 py-3 text-right tabular-nums" data-testid={`ab-B-${s.key}`}>{b.toLocaleString('nb-NO')}</td>
                                <td className={`px-4 py-3 text-right tabular-nums font-medium ${delta > 0 ? 'text-sage-dark' : delta < 0 ? 'text-crisis' : 'text-inkSoft'}`}>
                                    {delta > 0 ? '+' : ''}{delta}
                                </td>
                            </tr>
                        );
                    })}
                    <tr className="border-t border-sand bg-sand/20">
                        <td className="px-4 py-3 text-inkSoft text-xs uppercase tracking-wider">View → Signup</td>
                        <td className="px-4 py-3 text-right tabular-nums">{cellPct('A', 'signup', 'landing_view')}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{cellPct('B', 'signup', 'landing_view')}</td>
                        <td className="px-4 py-3 text-right tabular-nums">—</td>
                    </tr>
                    <tr className="border-t border-sand bg-sand/20">
                        <td className="px-4 py-3 text-inkSoft text-xs uppercase tracking-wider">View → First message</td>
                        <td className="px-4 py-3 text-right tabular-nums">{cellPct('A', 'first_message', 'landing_view')}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{cellPct('B', 'first_message', 'landing_view')}</td>
                        <td className="px-4 py-3 text-right tabular-nums">—</td>
                    </tr>
                    <tr className="border-t border-sand bg-sand/20">
                        <td className="px-4 py-3 text-inkSoft text-xs uppercase tracking-wider">View → Paid</td>
                        <td className="px-4 py-3 text-right tabular-nums">{cellPct('A', 'paid', 'landing_view')}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{cellPct('B', 'paid', 'landing_view')}</td>
                        <td className="px-4 py-3 text-right tabular-nums">—</td>
                    </tr>
                </tbody>
            </table>
            {rates && (
                <div className="px-4 py-2 text-[11px] text-inkSoft/70 border-t border-sand">
                    Unique-anon counts. Funnel events deduped per (event, anon_id). Paid = users with active subscription tagged with that variant.
                </div>
            )}
        </div>
    );
}

function ABVerdictCard({ sig }) {
    const v = sig?.verdict || {};
    const winnerStyle = v.winner === 'B'
        ? 'border-sage bg-sage/10 text-sage-dark'
        : v.winner === 'A'
            ? 'border-sage bg-sage/10 text-sage-dark'
            : 'border-sand bg-sand/30 text-inkSoft';
    return (
        <div className={`rounded-2xl border-2 p-4 mb-4 ${winnerStyle}`} data-testid="ab-verdict">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider mb-1">
                <FlaskConical className="w-3.5 h-3.5"/> Bayesian verdict
            </div>
            <div className="font-display text-lg font-semibold" data-testid="ab-verdict-message">
                {v.winner ? `🏆 Variant ${v.winner} wins` : '⏳ Inconclusive'}
                {v.metric && <span className="text-sm font-normal text-inkSoft"> · on {v.metric.replace('_', ' ')}</span>}
            </div>
            <p className="text-sm mt-1 text-ink/80">{v.message}</p>
        </div>
    );
}

function ABSignificanceTable({ sig }) {
    const metrics = [
        { key: 'signup', label: 'Signup' },
        { key: 'first_message', label: 'First message' },
        { key: 'paid', label: 'Paid' },
    ];
    const fmtPct = (v) => v === null || v === undefined ? '—' : `${v}%`;
    const fmtCi = (ci) => Array.isArray(ci) ? `[${ci[0]}%, ${ci[1]}%]` : '—';
    return (
        <div className="rounded-2xl border border-sand bg-white overflow-x-auto mt-4" data-testid="ab-significance-table">
            <div className="px-4 py-2 bg-sand/30 text-xs uppercase tracking-wider text-inkSoft border-b border-sand">
                Bayesian rates · over landing_view (Beta(1,1) prior, 20k samples)
            </div>
            <table className="w-full text-sm">
                <thead className="bg-sand/20 text-inkSoft text-[11px] uppercase tracking-wider">
                    <tr>
                        <th className="text-left px-4 py-2">Metric</th>
                        <th className="text-right px-4 py-2">Rate A</th>
                        <th className="text-right px-4 py-2">Rate B</th>
                        <th className="text-right px-4 py-2">P(B&gt;A)</th>
                        <th className="text-right px-4 py-2">CI95% (B−A)</th>
                        <th className="text-right px-4 py-2">Verdict</th>
                    </tr>
                </thead>
                <tbody>
                    {metrics.map(m => {
                        const r = sig.results?.[m.key] || {};
                        return (
                            <tr key={m.key} className="border-t border-sand" data-testid={`ab-sig-${m.key}`}>
                                <td className="px-4 py-2.5 text-ink">{m.label}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(r.rate_a_pct)}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums">{fmtPct(r.rate_b_pct)}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fmtPct(r.prob_b_beats_a_pct)}</td>
                                <td className="px-4 py-2.5 text-right tabular-nums text-xs">{fmtCi(r.ci95_diff_pct)}</td>
                                <td className={`px-4 py-2.5 text-right text-xs font-medium ${r.confident ? 'text-sage-dark' : 'text-inkSoft'}`}>
                                    {r.confident ? `🏆 ${r.winner}` : '—'}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

